import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsTile } from './AnalyticsTile';
import { ViewsLead, ViewsTopRow, type ViewsTileProps } from './ViewsTopRow';
import { useHasPermission } from '../../../auth';
import { tokens } from '../../../design';
import { specialistDetailPath } from '../../../routes';
import { formatViews } from '../_lib/formatViews';

const INITIALS_MAX = 2;

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, INITIALS_MAX)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

export const TopCompaniesTile = ({
  data,
  isLoading,
  isError,
  gridColumn,
}: ViewsTileProps): ReactElement => {
  const { t } = useTranslation();
  const canOpen = useHasPermission('verify_inspectors');
  return (
    <AnalyticsTile
      label={t('dashboard.views.topCompanies')}
      gridColumn={gridColumn}
      isLoading={isLoading}
      isError={isError}
      hasData={Boolean(data)}
    >
      {data && data.topCompanies.length === 0 ? (
        <Box sx={{ fontSize: 13, color: tokens.text.tertiary, py: 1 }}>
          {t('dashboard.views.empty')}
        </Box>
      ) : null}
      {data?.topCompanies.map((row, i) => (
        <ViewsTopRow
          key={row.profileId}
          rank={i + 1}
          lead={<ViewsLead round url={row.avatarUrl} fallback={initials(row.name)} />}
          name={row.name}
          chip={row.hidden ? t('dashboard.views.hidden') : null}
          count={
            <>
              {formatViews(row.total)}
              <Box
                component="small"
                sx={{
                  display: 'block',
                  fontSize: 10,
                  fontWeight: 500,
                  color: tokens.text.tertiary,
                }}
              >
                {t('dashboard.views.companyBreakdown', {
                  profile: formatViews(row.profileViews),
                  listings: formatViews(row.listingViews),
                })}
              </Box>
            </>
          }
          to={canOpen ? specialistDetailPath(row.profileId) : undefined}
        />
      ))}
    </AnalyticsTile>
  );
};
